import { useEffect, useRef, useState, useCallback } from 'react';
import './cables.css';
import { useProjectStore } from '../../store/useProjectStore';
import {
  getCables, estimateCables, getCablesStatus,
  getCablesPublishStatus, setCablesPublishStatus, uploadCablesToApp,
  type CablesPublishStatus,
} from '../../api/endpoints';
import type { CablePlanResult } from '../../types';

const ARCHETYPE_LABEL: Record<string, string> = {
  radial_routed: 'Radial · routed via trays',
  local_busbar_given: 'Busbar · lengths given',
  hybrid: 'Hybrid · busbar + radial',
  floorplan_only: 'Floor-plan only · no SLD',
  bus_loop: 'Bus / loop',
  unknown: 'Unknown',
};

function b64url(s: string): string {
  return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function planViewUrl(s3Uri: string | undefined, planName: string): string | null {
  if (!s3Uri) return null;
  return `/cables?pid=proj:${b64url(s3Uri)}:${b64url(planName)}`;
}

function PlanCard({ plan, s3Uri }: { plan: CablePlanResult; s3Uri?: string }) {
  const st = plan.strategy || {};
  const arch = st.archetype || 'unknown';
  const total = Math.round((plan.total_m || 0) * 10) / 10;
  const viewUrl = planViewUrl(s3Uri, plan.plan_name);
  return (
    <div className="cables-card">
      <div className="cables-card-head">
        <div className="cables-plan-name" title={plan.plan_name}>{plan.plan_name}</div>
        <div className="cables-plan-right">
          {viewUrl && <a className="cables-viewlink" href={viewUrl} target="_blank" rel="noreferrer">View on plan ↗</a>}
          <span className="cables-plan-total">{total.toLocaleString()} m</span>
        </div>
      </div>
      <div className="cables-tags">
        <span className="cables-tag arch">{ARCHETYPE_LABEL[arch] || arch}</span>
        {st.confidence && <span className="cables-tag">{st.confidence}</span>}
        {st.device_source && <span className="cables-tag src">{st.device_source}</span>}
      </div>
      {st.summary && <div className="cables-summary">{st.summary}</div>}
      {plan.error && <div className="cables-warn">⚠ {plan.error}</div>}
      {plan.provenance && (
        <details className="cables-prov" open>
          <summary>How we measured this — source of truth</summary>
          {plan.provenance.project_structure && (
            <div className="prov-row"><b>Project structure</b><span>{plan.provenance.project_structure}</span></div>
          )}
          {plan.provenance.source_of_truth && (
            <div className="prov-row"><b>Numbers come from</b><span>{plan.provenance.source_of_truth}</span></div>
          )}
          {plan.provenance.sld_usage && (
            <div className="prov-row"><b>Single-line diagram</b><span>{plan.provenance.sld_usage}</span></div>
          )}
          {plan.provenance.sources && plan.provenance.sources.length > 0 && (
            <div className="prov-row"><b>Source (board) placement</b>
              <span>{plan.provenance.sources.map((s, i) => (
                <div key={i}>• <b>{s.name}</b> — {s.placed_by}</div>
              ))}</span></div>
          )}
          {plan.provenance.grouping && (
            <div className="prov-row"><b>How devices are grouped</b><span>{plan.provenance.grouping}</span></div>
          )}
          {plan.provenance.routing_decision && (
            <div className="prov-row"><b>Routing decision</b><span>{plan.provenance.routing_decision}</span></div>
          )}
          {plan.provenance.excluded_groups && plan.provenance.excluded_groups.length > 0 && (
            <div className="prov-row"><b>Not electrical — no cable routed</b>
              <span>{plan.provenance.excluded_groups.join(' · ')}</span></div>
          )}
          {plan.provenance.separate_systems && plan.provenance.separate_systems.length > 0 && (
            <div className="prov-row"><b>Separate systems (own panel)</b>
              <span>{plan.provenance.separate_systems.map((x, i) => <div key={i}>• {x}</div>)}</span></div>
          )}
          {plan.provenance.scale && (
            <div className="prov-row"><b>Scale</b><span>{plan.provenance.scale}</span></div>
          )}
          {plan.provenance.warnings && plan.provenance.warnings.length > 0 && (
            <div className="prov-row"><b>Caveats</b>
              <span>{plan.provenance.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</span></div>
          )}
        </details>
      )}
      {plan.boq_items && plan.boq_items.length > 0 && (
        <table className="cables-table">
          <thead>
            <tr><th>Cable / group</th><th className="num">Derivation</th><th className="num">Qty</th><th>Method</th></tr>
          </thead>
          <tbody>
            {plan.boq_items.map((it, i) => (
              <tr key={i}>
                <td title={it.text}>{it.text}</td>
                <td className="num deriv">
                  {it.given_length_each_m != null
                    ? `${it.device_count ?? 0} × ${it.given_length_each_m} m`
                    : (it.cable_type || (it.device_count ? `${it.device_count} dev` : '—'))}
                </td>
                <td className="num">{it.quantity.toLocaleString()} {it.unit}</td>
                <td className="method">{it.method || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CablesTab() {
  const project = useProjectStore((s) => s.project);
  const cablesResult = useProjectStore((s) => s.cablesResult);
  const setCablesResult = useProjectStore((s) => s.setCablesResult);
  const setToast = useProjectStore((s) => s.setToast);

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
  const pollRef = useRef<number | null>(null);
  const [publishSt, setPublishSt] = useState<CablesPublishStatus | null>(null);
  const [uploading, setUploading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [instructions, setInstructions] = useState('');

  // Load any existing result + app publish state on open; prefill the
  // instructions textarea with what the last run used (side metadata).
  useEffect(() => {
    if (!cablesResult) {
      getCables().then((r) => { if (r) setCablesResult(r); }).catch(() => { /* not run yet */ });
    }
    getCablesPublishStatus().then((s) => { if (s) setPublishSt(s); }).catch(() => undefined);
    getCablesStatus()
      .then((st) => { if (st?.last_instructions) setInstructions(st.last_instructions); })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.project_id]);

  const doUpload = useCallback(async () => {
    setUploading(true);
    try {
      const r = await uploadCablesToApp();
      if (!r) throw new Error('upload failed');
      setToast(`Uploaded to app: ${r.boq_items} BoQ items · ${Math.round(r.total_m).toLocaleString()} m`, 'success');
      const s = await getCablesPublishStatus().catch(() => null);
      if (s) setPublishSt(s);
    } catch (e) {
      setToast(`Upload failed: ${e}`, 'error');
    } finally {
      setUploading(false);
    }
  }, [setToast]);

  const doToggle = useCallback(async () => {
    const next = !publishSt?.enabled;
    if (next && !window.confirm(
      'Enable the cables section in app.monco.ai for THIS project?\n\n' +
      'The current estimation will be uploaded and the beta cables section becomes visible to app users of this project.')) {
      return;
    }
    setToggling(true);
    try {
      const r = await setCablesPublishStatus(next);
      if (!r) throw new Error('request failed');
      setPublishSt((p) => ({ ...(p || { enabled: false }), enabled: r.enabled, published_at: new Date().toISOString() }));
      setToast(next ? 'Cables section enabled in app.monco.ai (beta)' : 'Cables section disabled in app.monco.ai', 'success');
    } catch (e) {
      setToast(`Failed: ${e}`, 'error');
    } finally {
      setToggling(false);
    }
  }, [publishSt?.enabled, setToast]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => () => stopPoll(), [stopPoll]);

  const run = useCallback(async () => {
    setRunning(true);
    setProgress(null);
    try {
      await estimateCables(instructions.trim());
    } catch (e) {
      setRunning(false);
      setToast(`Failed to start: ${e}`, 'error');
      return;
    }
    stopPoll();
    pollRef.current = window.setInterval(async () => {
      try {
        const st = await getCablesStatus();
        if (!st) return;
        if (st.total) setProgress({ processed: st.processed || 0, total: st.total });
        // pull partial/final result each tick so plans fill in progressively
        const r = await getCables().catch(() => null);
        if (r) setCablesResult(r);
        if (st.status === 'done' || st.status === 'error' || st.status === 'idle') {
          stopPoll();
          setRunning(false);
          setProgress(null);
          if (st.status === 'error') setToast(`Estimation failed: ${st.error || ''}`, 'error');
          else setToast('Cable estimation complete', 'success');
          const fr = await getCables().catch(() => null);
          if (fr) setCablesResult(fr);
        }
      } catch { /* keep polling */ }
    }, 3000);
  }, [instructions, setCablesResult, setToast, stopPoll]);

  const plans = cablesResult?.plans || [];
  const totalM = Math.round((cablesResult?.total_m || 0) * 10) / 10;
  const items = cablesResult?.boq_items?.length || 0;

  return (
    <div className="cables-tab">
      <div className="cables-header">
        <div>
          <h2>Cable estimation</h2>
          <div className="cables-sub">
            {cablesResult
              ? `${plans.length} plan${plans.length === 1 ? '' : 's'} · ${totalM.toLocaleString()} m · ${items} BoQ items`
              : 'Estimate cable quantities per floor plan from the annotation + strategy.'}
          </div>
        </div>
        <div className="cables-actions">
          {cablesResult && (
            <>
              <button
                className={`cables-app-toggle ${publishSt?.enabled ? 'on' : ''}`}
                onClick={doToggle}
                disabled={toggling}
                title={publishSt?.enabled
                  ? `Visible in app.monco.ai since ${publishSt.published_at ? new Date(publishSt.published_at).toLocaleString() : '—'}${publishSt.published_by ? ` (by ${publishSt.published_by})` : ''}. Click to disable.`
                  : 'Enable the beta cables section in app.monco.ai for this project only'}
              >
                <span className="cables-toggle-dot" />
                {toggling ? '…' : publishSt?.enabled ? 'Visible in app · beta' : 'Show in app · beta'}
              </button>
              <button
                className="cables-upload"
                onClick={doUpload}
                disabled={uploading || running}
                title={publishSt?.uploaded_at
                  ? `Push the current estimation to app.monco.ai (last upload ${new Date(publishSt.uploaded_at).toLocaleString()})`
                  : 'Push the current estimation to app.monco.ai'}
              >
                {uploading ? 'Uploading…' : '⬆ Upload to app'}
              </button>
            </>
          )}
          <button className="cables-run" onClick={run} disabled={running}>
            {running
              ? (progress ? `Running… ${progress.processed}/${progress.total}` : 'Starting…')
              : (cablesResult ? '↻ Re-run' : '⚡ Run cable estimation')}
          </button>
        </div>
      </div>
      <details className="cables-instructions">
        <summary>
          Instructions for analysis{instructions.trim() ? ' · will be sent with the next run' : ' · optional'}
        </summary>
        <div className="cables-instructions-body">
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder={'Corrections and guidance merged into the analysis, e.g.\n"The group \'V1\' is NOT a distribution board — never route cables from it."'}
            rows={4}
          />
          <div className="cables-instructions-foot">
            <span className="cables-instructions-hint">
              Sent along with the next run — hit Re-run to apply.
            </span>
          </div>
        </div>
      </details>
      {!cablesResult && !running && (
        <div className="cables-empty">
          No cable estimation yet. Click <b>Run cable estimation</b> — the strategy appears in seconds,
          and each floor plan's takeoff fills in as it completes.
        </div>
      )}

      <div className="cables-plans">
        {plans.map((p, i) => <PlanCard key={i} plan={p} s3Uri={project?.s3_uri} />)}
      </div>
    </div>
  );
}
